/**
 * Consent State Management
 * Handles persistence, validation, and consent mode updates
 */

import { consentConfig } from './consent-config';

export interface ConsentPreferences {
  necessary: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

export interface ConsentRecord {
  preferences: ConsentPreferences;
  timestamp: string;
  policyVersion: string;
  userAgent: string;
  language: string;
  ip?: string;
  sessionId: string;
}

export interface ConsentState {
  hasConsented: boolean;
  preferences: ConsentPreferences;
  timestamp: string | null;
  policyVersion: string;
}

const defaultPreferences: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

// Generate session ID
const generateSessionId = (): string => {
  const stored = sessionStorage.getItem('tracking_session_id');
  if (stored) return stored;
  
  const id = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('tracking_session_id', id);
  return id;
};

export const getSessionId = generateSessionId;

// Cookie utilities
const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  const domain = consentConfig.cookieDomain 
    ? `; domain=${consentConfig.cookieDomain}` 
    : '';
  
  document.cookie = `${name}=${encodeURIComponent(value)}${domain}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

const deleteCookie = (name: string): void => {
  const domain = consentConfig.cookieDomain 
    ? `; domain=${consentConfig.cookieDomain}` 
    : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}; path=/`;
};

// Load consent state
export const loadConsentState = (): ConsentState => {
  try {
    // Try cookie first
    const cookieValue = getCookie(consentConfig.cookieName);
    if (cookieValue) {
      const parsed = JSON.parse(cookieValue);
      
      // Check if policy version matches
      if (parsed.policyVersion !== consentConfig.policyVersion) {
        // Policy updated, need re-consent
        return {
          hasConsented: false,
          preferences: defaultPreferences,
          timestamp: null,
          policyVersion: consentConfig.policyVersion,
        };
      }
      
      // Check expiration (re-consent after 12 months)
      const consentDate = new Date(parsed.timestamp);
      const monthsOld = (Date.now() - consentDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
      if (monthsOld > 1) {
        return {
          hasConsented: false,
          preferences: defaultPreferences,
          timestamp: null,
          policyVersion: consentConfig.policyVersion,
        };
      }
      
      return {
        hasConsented: true,
        preferences: parsed.preferences,
        timestamp: parsed.timestamp,
        policyVersion: parsed.policyVersion,
      };
    }
    
    // Fallback to localStorage
    const localValue = localStorage.getItem(consentConfig.cookieName);
    if (localValue) {
      const parsed = JSON.parse(localValue);
      if (parsed.policyVersion === consentConfig.policyVersion) {
        return {
          hasConsented: true,
          preferences: parsed.preferences,
          timestamp: parsed.timestamp,
          policyVersion: parsed.policyVersion,
        };
      }
    }
  } catch (e) {
    console.error('Error loading consent state:', e);
  }
  
  return {
    hasConsented: false,
    preferences: defaultPreferences,
    timestamp: null,
    policyVersion: consentConfig.policyVersion,
  };
};

// Save consent state
export const saveConsentState = async (
  preferences: ConsentPreferences,
  language: string
): Promise<ConsentRecord> => {
  const record: ConsentRecord = {
    preferences: { ...preferences, necessary: true },
    timestamp: new Date().toISOString(),
    policyVersion: consentConfig.policyVersion,
    userAgent: navigator.userAgent,
    language,
    sessionId: getSessionId(),
  };
  
  // Save to cookie
  setCookie(
    consentConfig.cookieName,
    JSON.stringify({
      preferences: record.preferences,
      timestamp: record.timestamp,
      policyVersion: record.policyVersion,
    }),
    consentConfig.cookieExpireDays
  );
  
  // Backup to localStorage
  localStorage.setItem(
    consentConfig.cookieName,
    JSON.stringify({
      preferences: record.preferences,
      timestamp: record.timestamp,
      policyVersion: record.policyVersion,
    })
  );
  
  // Update Google Consent Mode
  updateGoogleConsentMode(preferences);
  
  return record;
};

// Clear consent (for withdraw)
export const clearConsent = (): void => {
  deleteCookie(consentConfig.cookieName);
  localStorage.removeItem(consentConfig.cookieName);
  
  // Reset Google Consent Mode
  updateGoogleConsentMode(defaultPreferences);
};

// Google Consent Mode v2 integration
export const updateGoogleConsentMode = (preferences: ConsentPreferences): void => {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('consent', 'update', {
    ad_storage: preferences.marketing ? 'granted' : 'denied',
    analytics_storage: preferences.analytics ? 'granted' : 'denied',
    ad_user_data: preferences.marketing ? 'granted' : 'denied',
    ad_personalization: preferences.personalization ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
};

// Initialize consent mode with denied state
export const initializeConsentMode = (): void => {
  // Ensure gtag is available
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  
  // Set default consent state (denied)
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
};

// Type declarations
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: (...args: unknown[]) => void;
    ttq: {
      load: (id: string) => void;
      page: () => void;
      track: (event: string, data?: Record<string, unknown>) => void;
    };
    lintrk: (action: string, data: Record<string, unknown>) => void;
    _linkedin_partner_id: string;
  }
}
